import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Client-side pre-validation for a staged batch of setting changes.
 *
 * The server's ConfigurationValidator is the authority and runs again on
 * PUT /api/settings/bulk — this mirror exists so the owner learns about an
 * invalid combination BEFORE saving, with the exact fix, instead of after a 400.
 * Keep every rule in step with
 * backend/app/services/validators/configuration_validator.py; a rule that only
 * lives here would be fake validation (§46), so each entry names its server rule.
 *
 * Returns [{ keys, message }] — the rows to mark and the sentence to show.
 */
export function validateDraftBatch(changes, resolvedSettings, metadataByKey = {}) {
  const problems = [];

  // Format-checked text (GSTIN, FSSAI, Day Change Time): the registry's `pattern`,
  // the same one the server enforces, so the field says what is wrong before Save.
  for (const [key, value] of Object.entries(changes)) {
    const meta = metadataByKey[key];
    if (!meta?.pattern) continue;
    let ok = true;
    try { ok = new RegExp(meta.pattern).test(String(value ?? "")); } catch { ok = true; }
    if (!ok) problems.push({ keys: [key], message: meta.pattern_message || `${meta.display_name || key} has the wrong format`, field: true });
  }
  const get = key =>
    key in changes ? changes[key] : String(resolvedSettings?.[key]?.value ?? "");
  const bool = key => get(key).toLowerCase() === "true";
  const num = key => {
    const parsed = Number(get(key));
    return Number.isFinite(parsed) ? parsed : NaN;
  };

  // configuration_validator.py: service charge enabled ⇒ percent > 0.
  if ("service_charge_enabled" in changes || "service_charge_pct" in changes) {
    if (bool("service_charge_enabled") && !(num("service_charge_pct") > 0)) {
      problems.push({
        keys: ["service_charge_enabled", "service_charge_pct"],
        message:
          "Service charge is switched on but its rate is 0 — set Service Charge % above zero, or switch Service Charge off.",
      });
    }
  }

  // configuration_validator.py: discounts enabled ⇒ cap > 0.
  if ("discount_enabled" in changes || "discount_max_pct" in changes) {
    if (bool("discount_enabled") && !(num("discount_max_pct") > 0)) {
      problems.push({
        keys: ["discount_enabled", "discount_max_pct"],
        message:
          "Discounts are enabled but the cap is 0 — set Maximum Discount % above zero, or turn Discounts off.",
      });
    }
  }

  return problems;
}

/**
 * Staged editing for the settings console (the "safe editing" contract).
 *
 * Every row writes into one draft map; nothing reaches the server until Save
 * Changes commits the whole batch through PUT /api/settings/bulk, which validates
 * the prospective configuration atomically and rejects it with a named reason if
 * a combination is invalid. Per-field saves are gone on purpose: they let each
 * keystroke mutate live outlet configuration and made cross-setting mistakes
 * untraceable.
 *
 *   drafts     {key: stringValue}  what the rows render
 *   baseline   {key: stringValue}  what the server last confirmed
 *   dirtyKeys  keys whose draft differs from baseline
 *   setDraft(key, value)           stage one change
 *   revert()                       drop every staged change back to baseline
 *   commit(saveFn)                 hand dirtyKeys to a save function; on failure
 *                                  the drafts stay staged (unsaved, not lost)
 */
export default function useSettingsDrafts(resolvedSettings) {
  const baseline = useMemo(() => {
    const map = {};
    for (const [key, entry] of Object.entries(resolvedSettings || {})) {
      map[key] = String(entry?.value ?? "");
    }
    return map;
  }, [resolvedSettings]);

  const [drafts, setDrafts] = useState({});
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | failed

  const dirtyKeys = useMemo(
    () => Object.keys(drafts).filter(key => drafts[key] !== baseline[key]),
    [drafts, baseline]
  );

  const stage = useCallback((key, value) => {
    setSaveState(state => (state === "failed" || state === "saved" ? "idle" : state));
    setDrafts(previous => ({ ...previous, [key]: String(value) }));
  }, []);

  const revert = useCallback(() => {
    setDrafts({});
    setSaveState("idle");
  }, []);

  const commit = useCallback(async saveFn => {
    if (!dirtyKeys.length) return true;
    setSaveState("saving");
    const sent = Object.fromEntries(dirtyKeys.map(key => [key, drafts[key]]));
    try {
      await saveFn(Object.entries(sent));
      // The server accepted the batch: drop what was sent (a key edited again
      // while the save was in flight keeps its newer draft). The pages used to do
      // this with a `setDrafts` they never had, so every successful save threw a
      // ReferenceError and the bar said "The server rejected these changes"
      // (floor report 2026-10-19).
      setDrafts(previous => {
        const next = { ...previous };
        for (const [key, value] of Object.entries(sent)) if (next[key] === value) delete next[key];
        return next;
      });
      setSaveState("saved");
      return true;
    } catch (error) {
      // The drafts stay exactly as staged: a failed save is unsaved work to fix,
      // not a reason to lose it.
      setSaveState("failed");
      throw error;
    }
  }, [dirtyKeys, drafts]);

  /** The value a row renders: its staged draft when it has one, else resolved. */
  const valueOf = useCallback(
    key => (key in drafts ? drafts[key] : baseline[key]),
    [drafts, baseline]
  );

  return {
    drafts,
    baseline,
    dirtyKeys,
    isDirty: dirtyKeys.length > 0,
    saveState,
    setSaveState,
    setDraft: stage,
    revert,
    commit,
    valueOf,
  };
}
